import { Observable, Subscription } from 'rxjs';
import {
  PauseDates,
  PauseErrorCode,
  PauseOutcome,
  PauseSubject,
} from '../types/subscription-pause.types';
import { pauseOutcomeCopy } from './subscription-pause.copy';
import { RELOAD_PAUSE_CODES } from './subscription-pause.util';

/** The actions a pause flow runs; implemented by SubscriptionPauseActionsService. */
export interface PauseFlowActions {
  pause(subject: PauseSubject, dates: PauseDates): Observable<PauseOutcome>;
  activate(subject: PauseSubject): Observable<PauseOutcome>;
  cancelScheduled(subject: PauseSubject): Observable<PauseOutcome>;
}

export interface PauseFlowOptions {
  /** Host-specific follow-up after any successful action: reload a list, refresh a lane event. */
  onDone?: (outcome: PauseOutcome) => void;
  /**
   * The card is out of date (changed elsewhere, or offered Pause on a membership that can never
   * be paused): reload so the host shows it. See RELOAD_PAUSE_CODES.
   */
  onStale?: () => void;
  /** Result modal header. Defaults to the plan name. */
  resultHeader?: (outcome: PauseOutcome, subject: PauseSubject) => string;
}

export interface PauseResultView {
  header: string;
  title: string;
  message: string;
}

/**
 * The state behind the pause modals, shared by every host (Customers tab, lane popups) so none
 * of them repeats the open / submit / busy / error / result wiring. Hosts bind their templates to
 * the public fields and call the methods from the presentational components' outputs.
 *
 * One action runs at a time; calls made while `busy` are ignored. Plain fields, so it works with
 * default (Eager) change detection. Call `destroy()` from the host's `ngOnDestroy`.
 */
export class PauseFlowController {
  /** Membership the pause modal is open for. */
  pauseTarget: PauseSubject | null = null;
  /** Membership whose scheduled pause is awaiting cancel confirmation. */
  cancelTarget: PauseSubject | null = null;
  /** Membership awaiting Activate now confirmation. */
  activateTarget: PauseSubject | null = null;
  busy = false;
  /** Membership an action is in flight for. */
  busySubscriptionId: string | null = null;
  /** Refusal shown inside the open modal (pause, activate or cancel-scheduled). */
  errorCode: PauseErrorCode | null = null;
  result: PauseResultView | null = null;

  private inFlight: Subscription | null = null;

  constructor(
    private readonly actions: PauseFlowActions,
    private readonly options: PauseFlowOptions = {}
  ) {}

  openPause(subject: PauseSubject): void {
    if (this.busy) {
      return;
    }
    this.pauseTarget = subject;
    this.errorCode = null;
  }

  closePause(): void {
    if (this.busy) {
      return;
    }
    this.pauseTarget = null;
    this.errorCode = null;
  }

  /** The user changed the dates after a refusal: the old message no longer applies. */
  clearError(): void {
    this.errorCode = null;
  }

  confirmPause(dates: PauseDates): void {
    const subject = this.pauseTarget;
    if (!subject || this.busy) {
      return;
    }
    this.run(subject, this.actions.pause(subject, dates), {
      done: () => (this.pauseTarget = null),
      failed: code => (this.errorCode = code),
    });
  }

  askActivate(subject: PauseSubject): void {
    if (this.busy) {
      return;
    }
    this.activateTarget = subject;
    this.errorCode = null;
  }

  dismissActivate(): void {
    if (this.busy) {
      return;
    }
    this.activateTarget = null;
    this.errorCode = null;
  }

  confirmActivate(): void {
    const subject = this.activateTarget;
    if (!subject || this.busy) {
      return;
    }
    this.run(subject, this.actions.activate(subject), {
      done: () => (this.activateTarget = null),
      failed: code => (this.errorCode = code),
    });
  }

  askCancelScheduled(subject: PauseSubject): void {
    if (this.busy) {
      return;
    }
    this.cancelTarget = subject;
    this.errorCode = null;
  }

  dismissCancelScheduled(): void {
    if (this.busy) {
      return;
    }
    this.cancelTarget = null;
    this.errorCode = null;
  }

  confirmCancelScheduled(): void {
    const subject = this.cancelTarget;
    if (!subject || this.busy) {
      return;
    }
    this.run(subject, this.actions.cancelScheduled(subject), {
      done: () => (this.cancelTarget = null),
      failed: code => (this.errorCode = code),
    });
  }

  closeResult(): void {
    this.result = null;
  }

  destroy(): void {
    this.inFlight?.unsubscribe();
    this.inFlight = null;
  }

  private run(
    subject: PauseSubject,
    action$: Observable<PauseOutcome>,
    handlers: { done?: () => void; failed: (code: PauseErrorCode) => void }
  ): void {
    this.busy = true;
    this.busySubscriptionId = subject.subscriptionId;
    this.inFlight = action$.subscribe({
      next: outcome => {
        this.settle();
        handlers.done?.();
        this.result = {
          header: this.options.resultHeader?.(outcome, subject) ?? subject.planName,
          ...pauseOutcomeCopy(outcome),
        };
        this.options.onDone?.(outcome);
      },
      error: (code: PauseErrorCode) => {
        this.settle();
        const failure = code || 'UNKNOWN';
        handlers.failed(failure);
        if (RELOAD_PAUSE_CODES.has(failure)) {
          this.options.onStale?.();
        }
      },
    });
  }

  private settle(): void {
    this.busy = false;
    this.busySubscriptionId = null;
    this.inFlight = null;
  }
}
